import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import Polish from '@uppy/locales/lib/pl_PL'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'

// Structured after uppy_comment_file_upload.js — see that file for the full
// rationale behind the single always-on MutationObserver, the
// isBeingDestroyed teardown guard, and live (not cached-at-init) DOM lookups
// for the submit button / CSRF token. This widget is used from 7 views, 2 of
// which (to_do_tasks/_new.html.erb, _new_ask_for_verification.html.erb) load
// into a Bootstrap modal via AJAX, so all three pitfalls apply directly.
//
// Unlike ContactAttemptFile's widget, every one of these 7 views has exactly
// one <form> under its .user-show wrapper (no sibling comment/contact-
// attempt form ever coexists here), so — like RecommendationFile's widget —
// no form-id/field-based disambiguation is needed to pick it out.
let uppyInstance;
let trackedDragDropArea;
let isBeingDestroyed = false;

function destroyUppyInstance() {
    if (uppyInstance) {
        isBeingDestroyed = true;
        uppyInstance.destroy();
        isBeingDestroyed = false;
        uppyInstance = null;
    }
    trackedDragDropArea = null;
}

function handleDomMutation() {
    if (trackedDragDropArea && !document.body.contains(trackedDragDropArea)) {
        destroyUppyInstance();
    }
    initToDoTaskFileUppyIfPresent();
}

function initToDoTaskFileUppyIfPresent() {
    const dragDropArea = document.querySelector('#to-do-task-file-uppy-drag-drop-area')

    if (!dragDropArea || dragDropArea.dataset.uppyInitialized === 'true') return;
    dragDropArea.dataset.uppyInitialized = 'true';
    trackedDragDropArea = dragDropArea;

    function getToDoTaskForm() {
        return $(dragDropArea).closest('.user-show').find('form').first();
    }

    function getSubmitButtons() {
        return getToDoTaskForm().find("input[type='submit']");
    }

    // form_for(@to_do_task) adds its own authenticity_token hidden field,
    // and several of these views add a second explicit one (rendered with
    // the same token this widget's data-token was rendered with) —
    // rails-ujs's refreshCSRFTokens rewrites every
    // input[name="authenticity_token"] in the DOM on load, so read the
    // form's own (last) field live, at upload time, rather than relying on
    // the snapshot baked into data-token at render time.
    function getCurrentAuthenticityToken() {
        const field = getToDoTaskForm().find('input[name="authenticity_token"]').last();
        return field.length ? field.val() : dragDropArea.dataset.token;
    }

    // Pages with multiple recommendations-table-style submit buttons render
    // each with its own distinct label. jQuery's .val() getter only ever
    // reads the *first* element in a multi-element selection, so capturing
    // one shared "original label" and writing it back to every button on
    // completion would collapse all of them to the first button's text (a
    // bug hit and fixed for recommendations/_form.html.erb's buttons — see
    // uppy_recommendation_file_upload.js). None of these 7 views currently
    // render more than one submit button, but storing each button's own
    // label on itself costs nothing and avoids reintroducing that bug if
    // that ever changes.
    function rememberOriginalLabels(submitButtons) {
        submitButtons.each(function () {
            if (this.dataset.originalSubmitLabel === undefined) {
                this.dataset.originalSubmitLabel = this.value;
            }
        });
    }

    function restoreOriginalLabels(submitButtons) {
        submitButtons.each(function () {
            if (this.dataset.originalSubmitLabel !== undefined) {
                this.value = this.dataset.originalSubmitLabel;
            }
        });
    }

    dragDropArea.style.width = `calc(100% - 20px)`;

    uppyInstance = new Uppy({
        autoProceed: true,
        restrictions: {
            maxFileSize: parseInt(dragDropArea.dataset.maxFileSize),
            maxNumberOfFiles: parseInt(dragDropArea.dataset.maxNumberOfFiles)
        },
        locale: Polish
    })

    uppyInstance.use(Dashboard, {
        inline: true,
        target: '#to-do-task-file-uppy-drag-drop-area',
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: '100%',
        showRemoveButtonAfterComplete: true
    })

    uppyInstance.use(AwsS3Multipart, {
        id: 'AwsS3Multipart',

        mode: 'custom',

        shouldUseMultipart: (_file) => true,

        createMultipartUpload: async (file) => {
            const response = await fetch('/s3/multipart', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    filename: file.name,
                    type: file.type
                })
            })
            const result = await response.json()
            return {
                uploadId: result.uploadId,
                key: result.key
            }
        },

        signPart: async (file, {uploadId, key, partNumber}) => {
            const res = await fetch(`/s3/multipart/${uploadId}/${partNumber}?key=${encodeURIComponent(key)}`, {
                method: 'GET'
            })

            const {url} = await res.json()
            return {
                url,
                headers: {'Content-Type': 'application/octet-stream'}
            }
        },

        completeMultipartUpload: async (file, {key, uploadId, parts}) => {
            const response = await fetch(`/s3/multipart/${uploadId}/complete?key=${encodeURIComponent(key)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    parts,
                    model: 'ToDoTaskFile',
                    token: getCurrentAuthenticityToken()
                })
            })
            const result = await response.json()

            // The file itself is already fully uploaded to S3 at this point —
            // this request only asks the server to persist the ToDoTaskFile
            // record (running e.g. the extension validation). A 4xx here
            // means that failed, so surface it instead of reporting success
            // with no record ever created.
            if (!response.ok) {
                throw new Error((result.errors || []).join(', ') || 'Nie udało się zapisać pliku')
            }

            uppyInstance.setFileState(file.id, {modelId: result.document_file_id})

            return {location: result.location}
        },

        abortMultipartUpload: async (file, {key, uploadId}) => {
            await fetch(`/s3/multipart/${uploadId}?key=${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })

    uppyInstance.on('file-added', () => {
        const submitButtons = getSubmitButtons();
        rememberOriginalLabels(submitButtons);
        submitButtons.prop('disabled', true).val('Trwa dodawanie plików');
    });

    uppyInstance.on('complete', () => {
        const submitButtons = getSubmitButtons();
        submitButtons.prop('disabled', false);
        restoreOriginalLabels(submitButtons);
    });

    uppyInstance.on('file-removed', (file) => {
        if (isBeingDestroyed) return;

        const submitButtons = getSubmitButtons();
        submitButtons.prop('disabled', false);
        restoreOriginalLabels(submitButtons);

        if (file.modelId) {
            fetch('/to_do_task_files/' + file.modelId, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                }
            })
        }
    })
}

document.addEventListener('turbolinks:before-render', destroyUppyInstance);
document.addEventListener('DOMContentLoaded', initToDoTaskFileUppyIfPresent);
document.addEventListener('turbolinks:load', initToDoTaskFileUppyIfPresent);

// The pack tag executes before <body> exists (no defer), so document.body
// isn't available yet at top level — start observing once it is.
function startObservingBody() {
    if (!document.body) {
        setTimeout(startObservingBody, 0);
        return;
    }
    new MutationObserver(handleDomMutation).observe(document.body, {childList: true, subtree: true});
    initToDoTaskFileUppyIfPresent();
}

startObservingBody();
